import React from "react";
import { Prompt, useClonePrompt, useDeletePrompt, useToggleFavoritePrompt, useSetActivePromptId } from "@/lib/prompts-store";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, Edit3, Copy, Trash2, Archive, MoreHorizontal, Share2, Star } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
interface PromptCardProps {
  prompt: Prompt;
  onOpenPlayground: () => void;
  onEdit: (prompt: Prompt) => void;
}
export function PromptCard({ prompt, onOpenPlayground, onEdit }: PromptCardProps) {
  const clonePrompt = useClonePrompt();
  const deletePrompt = useDeletePrompt();
  const toggleFavorite = useToggleFavoritePrompt();
  const setActivePromptId = useSetActivePromptId();
  const statusColors = {
    published: "bg-green-500/20 text-green-400 border-green-500/30",
    draft: "bg-slate-400/20 text-slate-400 border-slate-400/30",
    archived: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  };
  const handleTest = () => {
    setActivePromptId(prompt.id);
    onOpenPlayground();
  };
  const handleFork = () => {
    clonePrompt(prompt.id);
    toast.success("Prompt forked successfully");
  };
  const handleDelete = () => {
    deletePrompt(prompt.id);
    toast.error("Prompt deleted");
  };
  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(prompt.id);
    toast.info(`Prompt ${prompt.isFavorite ? 'removed from' : 'added to'} favorites`);
  };
  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Card className="group relative flex flex-col h-full overflow-hidden border-white/30 bg-card/60 backdrop-blur-xl hover:border-primary/40 hover:shadow-glow transition-all">
        <div className="p-5 space-y-4 flex-grow">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-bold text-md leading-snug pr-8">{prompt.name}</h3>
              <Badge variant="outline" className={`capitalize text-[10px] mt-2 ${statusColors[prompt.status]}`}>
                {prompt.status}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={handleFavorite}>
                <Star className={`w-4 h-4 transition-colors ${prompt.isFavorite ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'}`} />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(prompt)} className="gap-2"><Edit3 className="w-4 h-4" /> Edit</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleFork} className="gap-2"><Copy className="w-4 h-4" /> Fork</DropdownMenuItem>
                  <DropdownMenuItem className="gap-2"><Share2 className="w-4 h-4" /> Share</DropdownMenuItem>
                  <DropdownMenuItem className="gap-2"><Archive className="w-4 h-4" /> Archive</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDelete} className="text-destructive gap-2"><Trash2 className="w-4 h-4" /> Delete</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed h-[48px]">
            {prompt.content}
          </p>
          <div className="flex flex-wrap gap-1">
            {prompt.tags.map(tag => (
              <Badge key={tag} variant="secondary" className="text-[10px] capitalize">{tag}</Badge>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 p-4 border-t border-white/5 bg-muted/20">
          <div className="text-center">
            <p className="text-[10px] text-muted-foreground uppercase font-semibold">Usage</p>
            <p className="text-sm font-bold">{prompt.metrics.usageCount}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-muted-foreground uppercase font-semibold">Tokens</p>
            <p className="text-sm font-bold">{prompt.metrics.avgTokens}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-muted-foreground uppercase font-semibold">Success</p>
            <p className="text-sm font-bold text-primary">{prompt.metrics.successRate}%</p>
          </div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 backdrop-blur-[2px]">
            <Button size="lg" className="rounded-full bg-white text-black hover:bg-white/90 gap-2" onClick={handleTest}>
              <Play className="w-4 h-4 fill-current" />
              Test in Playground
            </Button>
        </div>
      </Card>
    </motion.div>
  );
}
